import { useState, useEffect, useCallback } from "react";
import { motion, Reorder } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowsDownUp, FloppyDisk, X, DotsSixVertical } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import EloMigrationPanel from "@/components/admin/EloMigrationPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

function DraggableRow({ player, index, totalPlayers, movePlayerToRank }) {
  return (
    <Reorder.Item
      value={player}
      id={player.id}
      className="flex items-center gap-2 px-4 py-3 bg-card border border-border rounded-lg mb-2 cursor-grab active:cursor-grabbing active:shadow-lg active:border-primary/50 transition-colors"
      data-testid={`drag-ranking-row-${index + 1}`}
      whileDrag={{ scale: 1.02, boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}
    >
      <DotsSixVertical className="w-5 h-5 text-muted-foreground flex-shrink-0" weight="bold" />
      <div className="flex items-center gap-1 flex-shrink-0">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          index === 0 ? 'rank-1' : index === 1 ? 'rank-2' : index === 2 ? 'rank-3' : 'bg-muted text-muted-foreground'
        }`}>{index + 1}</div>
        <select
          value={index + 1}
          onChange={(e) => movePlayerToRank(player.id, parseInt(e.target.value))}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="w-14 h-7 text-xs bg-muted border border-border rounded px-1 text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid={`move-to-rank-select-${index + 1}`}
        >
          {Array.from({ length: totalPlayers }, (_, i) => (
            <option key={i + 1} value={i + 1}>#{i + 1}</option>
          ))}
        </select>
      </div>
      <Avatar className="h-9 w-9 border border-border flex-shrink-0">
        <AvatarImage src={player.photo_url} alt={player.name} />
        <AvatarFallback className="bg-muted text-muted-foreground text-xs">{getInitials(player.name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{player.name}</p>
        <p className="text-xs text-muted-foreground">{player.tier === 'premier' ? 'Premier' : 'Challenger'}</p>
      </div>
      <span className="text-sm font-bold text-primary w-12 text-right">{player.total_points} pts</span>
    </Reorder.Item>
  );
}

export default function AdminRankingsTab() {
  const [rankings, setRankings] = useState([]);
  const [reorderList, setReorderList] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchRankings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rankings`);
      setRankings(res.data);
    } catch { toast.error("Failed to load rankings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRankings(); }, [fetchRankings]);

  const startEditing = () => {
    setReorderList(rankings.map(p => ({ ...p })));
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setReorderList([]);
  };

  const movePlayerToRank = (playerId, targetRank) => {
    const currentIdx = reorderList.findIndex(p => p.id === playerId);
    if (currentIdx === -1 || targetRank < 1 || targetRank > reorderList.length) return;
    const targetIdx = targetRank - 1;
    if (currentIdx === targetIdx) return;
    // Shift/insert: remove from current position, insert at target
    const updated = [...reorderList];
    const [player] = updated.splice(currentIdx, 1);
    updated.splice(targetIdx, 0, player);
    // Force new object references so Reorder.Group re-renders
    setReorderList(updated.map(p => ({ ...p })));
  };

  const saveRankings = async () => {
    setSaving(true);
    try {
      const playerIds = reorderList.map(p => p.id);
      await axios.put(`${API}/rankings/reorder`, { player_ids: playerIds }, { withCredentials: true });
      toast.success(`Rankings saved for ${playerIds.length} players`);
      setEditing(false);
      setReorderList([]);
      await fetchRankings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rankings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-4">
      <EloMigrationPanel />

      <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ArrowsDownUp className="w-5 h-5 text-primary" weight="bold" />
          {editing ? "Drag to Reorder Rankings" : "Manage Rankings"}
        </CardTitle>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={cancelEditing} disabled={saving} className="border-border" data-testid="cancel-reorder-btn">
                <X className="w-4 h-4 mr-1" />Cancel
              </Button>
              <Button size="sm" onClick={saveRankings} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-reorder-btn">
                <FloppyDisk className="w-4 h-4 mr-1" weight="fill" />{saving ? 'Saving...' : 'Save Rankings'}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={startEditing} className="border-border" data-testid="manage-rankings-btn">
              <ArrowsDownUp className="w-4 h-4 mr-1" />Edit Rankings
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div>
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground font-medium mb-2 border-b border-border">
              <span className="w-5" />
              <span className="w-24">Rank / Move</span>
              <span className="w-9" />
              <span className="flex-1">Player</span>
              <span className="w-12 text-right">Points</span>
            </div>
            <Reorder.Group axis="y" values={reorderList} onReorder={setReorderList} className="space-y-0" data-testid="drag-ranking-list">
              {reorderList.map((player, idx) => (
                <DraggableRow key={player.id} player={player} index={idx} totalPlayers={reorderList.length} movePlayerToRank={movePlayerToRank} />
              ))}
            </Reorder.Group>
          </div>
        ) : (
          <div className="space-y-1">
            {rankings.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-muted/20 transition-colors" data-testid={`ranking-row-${i + 1}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  i === 0 ? 'rank-1' : i === 1 ? 'rank-2' : i === 2 ? 'rank-3' : 'bg-muted text-muted-foreground'
                }`}>{i + 1}</div>
                <Avatar className="h-8 w-8 border border-border">
                  <AvatarImage src={p.photo_url} />
                  <AvatarFallback className="text-xs">{getInitials(p.name)}</AvatarFallback>
                </Avatar>
                <span className="flex-1 text-sm font-medium truncate">{p.name}</span>
                <Badge className={`text-[10px] ${p.tier === 'premier' ? 'tier-premier' : 'tier-challenger'}`}>
                  {p.tier === 'premier' ? 'Premier' : 'Challenger'}
                </Badge>
                <span className="font-heading font-bold text-primary text-sm w-10 text-right">{p.total_points}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
}
